import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { RoomCondition } from "building-types/rooms/roomTypesEnum";
import { DOMAIN_COLOR, type WallStructuralDomain } from "building-types/structural/structuralDomainTypes";
import { WallCondition } from "building-types/walls/wallType";
import { inchesSquaredNew, worldInchesNew } from "common/utils/nominalNumbers";
import uid from "common/utils/uid";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import type { Face } from "geom/brep";
import { faceNew } from "geom/brep";
import { polyLine2OnPlane } from "geom/plane";
import { polyLine2AABB2, polyLine2Area, polyLine2IsRectangle } from "geom/polyLine2";
import { polyLine3AABB3 } from "geom/polyLine3";

export function wallStructuralDomainNew(
  from: Pick<WallStructuralDomain, "resolvedBoundary" | "plane"> & Partial<WallStructuralDomain>,
): WallStructuralDomain {
  const { id = uid() } = from;
  const docEntityHandle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: { entityType: "WALL_STRUCTURAL_DOMAIN", id },
  };
  const aabb2 = polyLine2AABB2(from.resolvedBoundary);
  const boundary3 = polyLine2OnPlane(from.plane, from.resolvedBoundary);
  const aabb3 = polyLine3AABB3(boundary3);
  const entityNode = BuildingGenerationContext.indexAddNode(docEntityHandle, aabb3);
  if (from.leftRooms) {
    for (const room of from.leftRooms) {
      BuildingGenerationContext.indexRelate(entityNode, "ADJACENT_TO", room);
    }
  }
  if (from.rightRooms) {
    for (const room of from.rightRooms) {
      BuildingGenerationContext.indexRelate(entityNode, "ADJACENT_TO", room);
    }
  }
  return {
    id,
    docEntityHandle,
    color: DOMAIN_COLOR,
    name: "Wall Structural Domain",
    parentHandles: [],
    layers: [],
    leftRooms: [],
    rightRooms: [],
    pickHandle: docEntityHandle,
    condition: WallCondition.EXTERIOR_INTERIOR,
    leftRoomCondition: RoomCondition.UNCONDITIONED,
    rightRoomCondition: RoomCondition.UNCONDITIONED,
    levelIndex: 0,
    bottomGeneratingLevel: undefined,
    ...from,
    entityType: "WALL_STRUCTURAL_DOMAIN",
    height: worldInchesNew(aabb2.maxX - aabb2.minX),
    length: worldInchesNew(aabb2.maxY - aabb2.minY),
    area: inchesSquaredNew(polyLine2Area(from.resolvedBoundary)),
    isRectangular: polyLine2IsRectangle(from.resolvedBoundary),
  };
}

export function wallStructuralDomainAsFace(domain: WallStructuralDomain): Face {
  return faceNew([domain.resolvedBoundary], domain.plane);
}
